import { Button, DatePicker, Select, Space } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import React from 'react'

import { SimpleCustomerDto } from 'enrollment-platform/api/persons/dto/simple-customer.dto'
import { CensusClientFilter } from '../types/census-client-filter'

const { RangePicker } = DatePicker

export interface CensusDataFilterProps {
  filter?: CensusClientFilter
  onFilterChange: (filter: CensusClientFilter) => void
  clients?: SimpleCustomerDto[]
  className?: string
}

/**
 * Filter component for census data with company search and date range.
 * Test clients are automatically filtered from the dropdown when filter.includeTestCompanies is false.
 *
 * @param filter - Current filter state
 * @param onFilterChange - Callback when filter changes
 * @param clients - List of available clients for company search
 * @param className - Optional CSS class name
 */
const CensusDataFilter: React.FC<CensusDataFilterProps> = ({
  filter,
  onFilterChange,
  clients = [],
}) => {
  const filteredClients =
    filter?.includeTestCompanies === true
      ? clients
      : clients.filter((c) => c.isTestClient !== true)
  const selectedClientId = filter?.selectedClient?.id
  const isSelectedClientInFilteredList = filteredClients.some(
    (c) => c.id === selectedClientId
  )

  const handleCompanyChange = (clientId: string | undefined) => {
    const selectedClient = clientId
      ? filteredClients.find((c) => c.id === clientId)
      : undefined
    onFilterChange({ ...filter, selectedClient })
  }

  const handleDateRangeChange = (
    dates: [Dayjs | null, Dayjs | null] | null
  ) => {
    if (dates && dates[0] && dates[1]) {
      onFilterChange({
        ...filter,
        dateFrom: dates[0].startOf('day').toISOString(),
        dateTo: dates[1].endOf('day').toISOString(),
      })
    } else {
      onFilterChange({
        ...filter,
        dateFrom: undefined,
        dateTo: undefined,
      })
    }
  }

  const handleClearFilters = () => {
    onFilterChange({
      includeTestCompanies: filter?.includeTestCompanies ?? true,
      selectedClient: undefined,
      dateFrom: undefined,
      dateTo: undefined,
    })
  }

  const hasActiveFilters =
    filter?.selectedClient || filter?.dateFrom || filter?.dateTo

  // Parse ISO string back to Dayjs for display
  const dateRangeValue: [Dayjs, Dayjs] | null =
    filter?.dateFrom && filter?.dateTo
      ? [dayjs(filter.dateFrom).local(), dayjs(filter.dateTo).local()]
      : null

  return (
    <div
      style={{
        background: 'transparent',
        border: 'none',
        width: '100%',
        justifyContent: 'flex-end',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
      }}
    >
      <Space align="center" wrap size="middle">
        {/* Clear Filters Button */}
        <Button
          type="link"
          onClick={handleClearFilters}
          style={{ visibility: hasActiveFilters ? 'visible' : 'hidden' }}
        >
          Clear filters
        </Button>

        {/* Company Search */}
        <Select
          showSearch
          allowClear
          placeholder="Search company"
          style={{ minWidth: 300, width: '100%' }}
          optionFilterProp="label"
          value={isSelectedClientInFilteredList ? selectedClientId : undefined}
          onChange={handleCompanyChange}
          options={filteredClients.map((client) => ({
            value: client.id,
            label: client.name,
          }))}
        />

        {/* Date Range Picker */}
        <RangePicker
          placeholder={['Start Date', 'End Date']}
          value={dateRangeValue}
          onChange={handleDateRangeChange}
          format="YYYY-MM-DD"
          allowClear
        />
      </Space>
    </div>
  )
}

export default CensusDataFilter
